/**
 * copyText — clipboard write that works on non-secure HTTP origins.
 *
 * navigator.clipboard.writeText() is gated to secure contexts (HTTPS or
 * localhost). On plain HTTP it throws "permission denied" or is undefined,
 * which our UI was silently swallowing.
 *
 * Strategy: try the modern API first; on failure or absence, fall back to
 * the legacy execCommand("copy") via a hidden textarea — works on every
 * browser that pre-dates the Clipboard API restriction. Returns true iff
 * the copy actually succeeded.
 */
export async function copyText(text: string): Promise<boolean> {
  // Modern, secure-context path.
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy path */
    }
  }
  // Legacy fallback — works on http origins.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    ta.setAttribute("readonly", "");
    document.body.appendChild(ta);
    const selection = document.getSelection();
    const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (previousRange && selection) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }
    return ok;
  } catch {
    return false;
  }
}
